// src/components/js/TaxonomyComponent.js
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import "../css/TaxonomyComponent.css";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";

const TaxonomyComponent = () => {
  const [taxonomy, setTaxonomy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedFormat, setSelectedFormat] = useState("");
  const [counts, setCounts] = useState({});
  const navigate = useNavigate();

  // Dataset order same as LeftSidebar
  const DATASET_ORDER = [
    "GenomeAssembly",
    "BioProject",
    "BioSample",
    "Nucleotide",
    "SRA",
    "Taxonomy",
    "Gene",
    "GeoDataset",
    "Protein",
    "BioAssay",
    "Pathway",
    "Substance",
    "PubMed",
    "Bookshelf",
    "PMCArticle",
  ];

  // Map dataset names to frontend routes
  const DATASET_ROUTES = {
    GenomeAssembly: "/genomeassembly",
    BioProject: "/bioproject",
    BioSample: "/biosample",
    Nucleotide: "/nucleotide",
    SRA: "/sra",
    Taxonomy: "/taxonomy",
    Gene: "/gene",
    GeoDataset: "/geodata",
    Protein: "/protein",
    BioAssay: "/bioassay",
    Pathway: "/pathway",
    Substance: "/substance",
    PubMed: "/pubmed",

    // ✅ FIXED HERE
    Bookshelf: "/book",

    PMCArticle: "/pmc",
  };

  // Fetch taxonomy data
  const fetchTaxonomyData = useCallback(
    async (currentPage = page) => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`/api/taxonomy/?page=${currentPage}`);
        const data = res.data || {};
        setTaxonomy(data.results || []);

        const count = Number(data.count || 0);
        const pageSize = Number(data.page_size || 20);
        setTotalPages(pageSize > 0 ? Math.max(1, Math.ceil(count / pageSize)) : 1);
      } catch (err) {
        console.error("fetchTaxonomyData error:", err);
        setError(
          "❌ Failed to fetch taxonomy data. Please check your connection or try again later."
        );
      } finally {
        setLoading(false);
      }
    },
    [page]
  );

  // Fetch dataset counts
  const fetchCounts = useCallback(async () => {
    const endpoints = {
      Gene: "/api/gene/counts/",
      Nucleotide: "/api/nucleotide/counts/",
      Taxonomy: "/api/taxonomy/counts/",
      GenomeAssembly: "/api/genomeassembly/counts/",
      PubMed: "/api/pubmed/counts/",
      Protein: "/api/protein/counts/",

      // NOTE: backend still uses /api/book/
      Bookshelf: "/api/book/counts/",

      GeoDataset: "/api/geo/counts/",
      BioAssay: "/api/bioassay/counts/",
      Pathway: "/api/pathway/counts/",
      Substance: "/api/substance/counts/",
      SRA: "/api/sra/counts/",
      BioProject: "/api/bioproject/counts/",
      BioSample: "/api/biosample/counts/",
      PMCArticle: "/api/pmc/counts/",
    };

    const keys = Object.keys(endpoints);

    try {
      const promises = keys.map((k) =>
        axios
          .get(endpoints[k])
          .then((r) => {
            const d = r.data || {};
            if (typeof d.count === "number") return { key: k, count: d.count };
            if (typeof d.total_count === "number") return { key: k, count: d.total_count };
            if (typeof d === "number") return { key: k, count: d };
            return { key: k, count: 0 };
          })
          .catch(() => ({ key: k, count: 0 }))
      );

      const results = await Promise.all(promises);
      const updated = {};
      results.forEach((r) => {
        updated[r.key] = r.count;
      });
      setCounts(updated);
    } catch (err) {
      console.error("fetchCounts error:", err);
      setCounts({});
    }
  }, []);

  useEffect(() => {
    fetchTaxonomyData(page);
  }, [fetchTaxonomyData, page]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleFormatDownload = async () => {
    if (!selectedFormat) return;
    setError(null);

    try {
      const res = await axios.get(
        `/api/taxonomy/download/${selectedFormat}/`,
        { responseType: "blob" }
      );

      let filename = `taxonomy_dataset.${selectedFormat === "json" ? "zip" : selectedFormat}`;

      const contentDisposition = res.headers && res.headers["content-disposition"];
      if (contentDisposition) {
        const match = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^;"']+)/i);
        if (match && match[1]) {
          filename = decodeURIComponent(match[1]);
        }
      }

      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error("download error:", err);
      setError(`❌ Failed to download ${selectedFormat.toUpperCase()} file.`);
    }
  };

  return (
    <div className="taxonomy-container">
      <button className="back-button" onClick={() => navigate("/")}>
        <FaArrowLeft /> Back to Home
      </button>

      <header className="taxonomy-header">
        <h2>
          🌱 Taxonomy (<i>Eragrostis tef</i>)
        </h2>

        <div className="taxonomy-actions">
          <div className="dropdown-download">
            <select
              value={selectedFormat}
              onChange={(e) => setSelectedFormat(e.target.value)}
              className="format-select"
            >
              <option value="">Select Format</option>
              <option value="tsv">TSV</option>
              <option value="json">JSON (zip)</option>
              <option value="xml">XML</option>
            </select>

            <button
              onClick={handleFormatDownload}
              className="format-download-button"
              disabled={!selectedFormat}
            >
              ⬇️ Download
            </button>
          </div>

          <a href="/genomeassembly" target="_blank" rel="noopener noreferrer">
            <strong>🧬 Genome Info</strong>
          </a>

          <a
            href="https://www.ncbi.nlm.nih.gov/datasets/taxonomy/tree/?taxon=110835"
            target="_blank"
            rel="noopener noreferrer"
          >
            <strong>
              <u>Taxonomy Browser</u>
            </strong>
          </a>

          <a
            href="https://blast.ncbi.nlm.nih.gov/Blast.cgi?PAGE_TYPE=BlastSearch&QUERY=txid110835[ORGN]"
            target="_blank"
            rel="noopener noreferrer"
          >
            🔬 <strong>Run BLAST</strong>
          </a>
        </div>
      </header>

      {loading ? (
        <div className="taxonomy-loading">⏳ Loading taxonomy data...</div>
      ) : error ? (
        <div className="taxonomy-error">{error}</div>
      ) : (
        <>
          <div className="taxonomy-list">
            {taxonomy.length > 0 ? (
              taxonomy.map((taxon) => (
                <div key={taxon.id ?? taxon.tax_id} className="taxonomy-card">
                  <h3>
                    <u>
                      <i>
                        <a
                          href={`https://www.ncbi.nlm.nih.gov/Taxonomy/Browser/wwwtax.cgi?id=${taxon.tax_id}`}
                          className="taxonomy-link"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {taxon.scientific_name || "Unnamed Species"}
                        </a>
                      </i>
                    </u>
                  </h3>

                  <div className="taxonomy-fields">
                    <p><strong>Tax ID:</strong> {taxon.tax_id ?? "-"}</p>
                    <p><strong>Rank:</strong> {taxon.rank || "-"}</p>
                    <p><strong>Synonyms:</strong> {taxon.synonyms || "-"}</p>
                    <p><strong>Common Names:</strong> {taxon.common_names || "-"}</p>
                    <p>
                      <strong>Genetic Code:</strong>{" "}
                      <a
                        href={`https://www.ncbi.nlm.nih.gov/Taxonomy/Utils/wprintgc.cgi#${taxon.genetic_code || "1"}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {taxon.genetic_code ? `Code ${taxon.genetic_code}` : "Standard"}
                      </a>
                    </p>
                    <p>
                      <strong>Lineage:</strong>{" "}
                      {Array.isArray(taxon.lineage)
                        ? taxon.lineage.join(" > ")
                        : taxon.lineage || "-"}
                    </p>
                  </div>

                  {taxon.tax_id && (
                    <p className="taxonomy-ncbi">
                      <strong>🔗 NCBI:</strong>{" "}
                      <a
                        href={`https://www.ncbi.nlm.nih.gov/Taxonomy/Browser/wwwtax.cgi?id=${taxon.tax_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View NCBI Entry
                      </a>
                    </p>
                  )}
                </div>
              ))
            ) : (
              <p className="taxonomy-empty">No taxonomy data found.</p>
            )}
          </div>

          <div className="taxonomy-counts">
            <h3>📊 Dataset Counts</h3>

            {Object.keys(counts).length > 0 ? (
              <table>
                <thead>
                  <tr>
                    <th>Dataset</th>
                    <th>Count</th>
                  </tr>
                </thead>

                <tbody>
                  {DATASET_ORDER.map((dataset) => (
                    <tr key={dataset}>
                      <td>
                        <Link to={DATASET_ROUTES[dataset] || "/"} className="dataset-link">
                          {dataset}
                        </Link>
                      </td>
                      <td>
                        <Link to={DATASET_ROUTES[dataset] || "/"} className="count-link">
                          {counts[dataset] ?? 0}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>No dataset counts available.</p>
            )}
          </div>

          <div className="pagination-container">
            <button onClick={() => handlePageChange(page - 1)} disabled={page <= 1}>
              ⬅️ Prev
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages}>
              Next ➡️
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default TaxonomyComponent;
